// DE-INITIATE (founder-approved 2026-10-29): a self-hosted digital employee starts a
// conversation itself — a new thread in a channel it is a member of, or a DM with a person it
// reports to (or who assigned the task in question) — under the rules the server enforces:
//
//   scope        only in channels the DE is a member of, only about work its seat owns or
//                supervises (a task or deadline assigned to or created by such a seat, a KPI on
//                one, an approval it asked for or decides)
//   relevance    every start names what it is `about` (a card, KPI, deadline or approval),
//                shown under the post as a link chip
//   rate         one unprompted thread per channel (or DM) per day per DE, plus declared
//                routines once a day each; starts within an hour of its last one collapse into
//                a reply in that thread; replies in its own threads (`post_message`) are free
//   quiet hours  nothing unprompted in the recipients' quiet hours: it waits (`queued`) and the
//                `chat.de_initiation_send` job posts it when they end, unless it is about a
//                pending approval whose task is due before then
//   one home     the same `about` item is not started in a second channel or DM within 24 h
//   people       only mentioned people are notified; a person who muted the channel or set it
//                to `none` is not mentioned; nobody else is woken (no agent, no @channel)
//   channel      `channels.agentRules` `initiate` narrows the above (`routines`, `off`); a
//                `silent` channel takes declared routines only
//   admins       company settings `de_initiation` turn it off company-wide or per DE
// Every outcome, refusals included, is a `cell.de_initiation` row: the audit. A refusal is a 200
// with `outcome: "refused"` and its code and words (the row is written, so the refusal stays on
// record). Managed agents, crews and AGs keep posting only where they are bound.
import { z } from "zod";
import {
  DeInitiationAboutKind,
  DeInitiationOutcome,
  DeInitiationRefusal,
  DeInitiationTarget,
} from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const chat = { feature: "chat" } as const;

/** What an unprompted post is about: the link chip under it. */
export const DeInitiationAbout = z
  .strictObject({ kind: DeInitiationAboutKind, id: Uuid })
  .meta({ id: "DeInitiationAbout" });

export const DeInitiationCreate = z
  .strictObject({
    /** The DE's own id for this start: a retry with it returns the first outcome. */
    client_id: z.string().min(1).max(128),
    /** A channel the DE is a member of (a new thread there)… */
    channel_id: Uuid.optional(),
    /** …or a person: the DM with them (reporting chain, or who assigned the task). */
    person_membership_id: Uuid.optional(),
    about: DeInitiationAbout,
    /** The new thread's name in a channel (default: from the text). */
    title: z.string().trim().min(1).max(200).optional(),
    body_markdown: z.string().trim().min(1).max(40_000),
    /** People to notify (`person:<membership id>`); only people, never agents or @channel. */
    mentions: z
      .array(
        z
          .string()
          .max(43)
          .regex(/^person:[0-9a-f-]{36}$/),
      )
      .max(20)
      .optional(),
    /** A routine the company declared for this DE and channel (daily status and the like). */
    routine: z
      .string()
      .max(64)
      .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/)
      .optional(),
    /**
     * It needs an answer: the person is mentioned (their inbox's mention item), and an approval
     * it is about is linked as its card (the approval's own inbox item).
     */
    needs_answer: z.boolean().optional(),
  })
  .refine((b) => (b.channel_id === undefined) !== (b.person_membership_id === undefined), {
    message: "send channel_id or person_membership_id (one of them)",
    path: ["channel_id"],
  })
  .meta({ id: "DeInitiationCreate" });

export const DeInitiation = z
  .object({
    id: Uuid,
    outcome: DeInitiationOutcome,
    /** Why it was refused (with `detail` in plain words); null otherwise. */
    refusal: DeInitiationRefusal.nullable(),
    detail: z.string().nullable(),
    target: DeInitiationTarget,
    channel_id: Uuid.nullable(),
    thread_id: Uuid.nullable(),
    message_id: Uuid.nullable(),
    about: DeInitiationAbout,
    routine: z.string().nullable(),
    /** A queued start: when it goes. */
    deliver_after: Timestamp.nullable(),
    /** What waits on the person (`needs_answer`): their mention, or the approval's card. */
    waiting_on: z.object({ kind: z.enum(["mention", "approval"]), id: Uuid }).nullable(),
    /** Mentions left out: not a person here, or the person muted the channel or set it to none. */
    dropped_mentions: z.array(z.string()),
    /** The channel's standing instructions to its agents (they narrow these rules). */
    instructions: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "DeInitiation" });
export type DeInitiation = z.infer<typeof DeInitiation>;

export const deInitiateRoutes = {
  "deInitiations.create": defineRoute({
    ...chat,
    id: "deInitiations.create",
    method: "post",
    path: `${base}/agent/initiations`,
    tag: "channels",
    summary:
      "start_thread: a self-hosted DE starts a thread in a channel or a DM with a person, about a card, KPI, deadline or approval",
    description:
      "DE-INITIATE. The DE's agent key (or its run). Every rule is the server's: scope, the " +
      "`about` item, one a day per channel (routines apart; an hour's starts collapse into one " +
      "reply), quiet hours (queued), one home per item for 24 h, people only by mention, the " +
      "channel's `initiate` rule and the company's `de_initiation` setting. A refusal answers " +
      '200 with `outcome: "refused"`, its code and words, and is kept as the audit record.',
    access: "agent_key",
    params: CompanyParams,
    body: DeInitiationCreate,
    response: { status: 200, schema: DeInitiation },
    errors: ["conflict"],
    emits: ["message.created", "topic.created", "channel.created", "approval.requested"],
  }),
} as const;
